import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertTriangle, RefreshCw, ChevronDown, ChevronUp, User,
  MapPin, Clock, Car, Hash, Calendar, Shield, CheckCircle,
  XCircle, Activity, Info, ArrowRight, Phone, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Log entry badge ──────────────────────────────────────────────────────────
const ActionBadge = ({ action }) => {
  const map = {
    ASSIGNED: 'bg-green-100 text-green-700 border-green-200',
    MANUAL_ASSIGNMENT_BY_ADMIN: 'bg-blue-100 text-blue-700 border-blue-200',
    SELLER_REASSIGNED_AVAILABILITY: 'bg-purple-100 text-purple-700 border-purple-200',
    attempt_failed: 'bg-red-100 text-red-700 border-red-200',
    no_partners_in_city: 'bg-gray-100 text-gray-600 border-gray-200',
    partner_not_available_on_date: 'bg-orange-100 text-orange-700 border-orange-200',
    partner_slot_occupied: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };
  return (
    <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border ${map[action] || 'bg-gray-100 text-gray-600'}`}>
      {action?.replace(/_/g, ' ')}
    </span>
  );
};

// ─── Single unassigned verification card ─────────────────────────────────────
const VerificationCard = ({ item, partners, onManualAssign, onRefresh }) => {
  const { schedule, vehicle, assignment_log } = item;
  const [expanded, setExpanded] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [assigning, setAssigning] = useState(false);
  const { token } = useAuth();

  const statusColor = (s) => {
    if (s === 'needs_seller_reassignment') return 'bg-red-100 text-red-700 border-red-200';
    if (s === 'no_partner_available') return 'bg-orange-100 text-orange-700 border-orange-200';
    if (s === 'assigned') return 'bg-green-100 text-green-700 border-green-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const handleManualAssign = async () => {
    if (!selectedPartner) { toast.error('Select a partner first'); return; }
    setAssigning(true);
    try {
      await axios.post(`${API}/admin/manual-assign-partner`,
        { vehicle_id: schedule.vehicle_id, partner_id: selectedPartner },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Partner assigned successfully!');
      onManualAssign();
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assignment failed');
    } finally { setAssigning(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN') : '—';

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">

      {/* Card header */}
      <div className={`px-5 py-3 border-b ${schedule.status === 'needs_seller_reassignment' ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {schedule.status === 'needs_seller_reassignment'
              ? <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
              : <Clock className="h-5 w-5 text-orange-500 flex-shrink-0" />
            }
            <div>
              <p className="font-bold text-gray-900 text-sm">
                {vehicle ? `${vehicle.brand_name} ${vehicle.model_name}` : 'Unknown Vehicle'}
              </p>
              <p className="text-xs text-gray-500 font-mono">{vehicle?.registration_no} · {schedule.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs border ${statusColor(schedule.status)}`}>
              {schedule.status?.replace(/_/g, ' ')}
            </Badge>
            <span className="text-xs text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded-lg">
              Attempts: <b>{assignment_log?.retry_count ?? schedule.retry_count ?? 0}</b>
            </span>
          </div>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5 space-y-4">
        {/* Vehicle + seller info */}
        <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-2"><Car className="h-4 w-4 text-gray-400" />{vehicle?.vehicle_type || '—'}</div>
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" />{schedule.city}</div>
          <div className="flex items-center gap-2"><User className="h-4 w-4 text-gray-400" />{schedule.seller_name || '—'}</div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" />{schedule.seller_phone || '—'}</div>
          <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" />Created: {fmtDate(schedule.created_at)}</div>
          <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gray-400" />Last attempt: {fmtDate(schedule.last_attempt_at)}</div>
        </div>

        {/* Seller availability slots */}
        {schedule.availability_slots?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Seller's Requested Slots</p>
            <div className="flex flex-wrap gap-2">
              {schedule.availability_slots.map((slot, i) => (
                <span key={i} className="text-xs bg-gray-100 border border-gray-200 px-3 py-1.5 rounded-lg text-gray-700 font-medium">
                  📅 {slot.date} · {slot.from_time}–{slot.to_time}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Status message to seller */}
        {schedule.seller_notification && (
          <div className={`p-3 rounded-xl text-xs border ${schedule.status === 'needs_seller_reassignment' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
            <div className="flex items-start gap-2"><Info className="h-4 w-4 flex-shrink-0 mt-0.5" />{schedule.seller_notification}</div>
          </div>
        )}

        {/* Manual assignment */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center p-4 bg-gray-50 rounded-xl border border-gray-200">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800 mb-2">Manually Assign Partner</p>
            <div className="flex gap-3 flex-wrap">
              <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                <SelectTrigger className="w-56 bg-white h-9 text-sm">
                  <SelectValue placeholder="Select partner…" />
                </SelectTrigger>
                <SelectContent>
                  {partners.filter(p => p.city === schedule.city || !p.city).map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.partner_uid || 'No UID'}) · {p.city}
                    </SelectItem>
                  ))}
                  {/* Show all partners if none in city */}
                  {partners.filter(p => p.city === schedule.city).length === 0 && partners.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} ({p.partner_uid || 'No UID'}) · {p.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleManualAssign} disabled={assigning || !selectedPartner}
                className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm px-5">
                {assigning ? 'Assigning…' : 'Assign Now'}
              </Button>
            </div>
          </div>
        </div>

        {/* Expand/collapse log */}
        <button onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 font-medium transition">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {expanded ? 'Hide' : 'View'} Assignment Attempt Logs ({assignment_log?.entries?.length || 0} entries)
        </button>

        {/* Log entries */}
        <AnimatePresence>
          {expanded && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <div className="bg-gray-900 px-4 py-2 flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5 text-green-400" />
                  <span className="text-xs text-green-400 font-mono">Assignment Logs · {assignment_log?.entries?.length || 0} entries</span>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                  {(!assignment_log?.entries?.length) ? (
                    <p className="text-xs text-gray-400 p-4 text-center">No log entries yet</p>
                  ) : (
                    [...(assignment_log.entries || [])].reverse().map((entry, i) => (
                      <div key={i} className={`px-4 py-2.5 ${entry.action === 'ASSIGNED' || entry.action === 'MANUAL_ASSIGNMENT_BY_ADMIN' ? 'bg-green-50' : ''}`}>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-[10px] text-gray-400 font-mono">{entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-IN') : '—'}</span>
                          <ActionBadge action={entry.action} />
                          {entry.partner_name && <span className="text-[10px] text-blue-600 font-semibold">👤 {entry.partner_name}</span>}
                          {entry.slot_date && <span className="text-[10px] text-gray-500">📅 {entry.slot_date} {entry.slot_from}–{entry.slot_to}</span>}
                        </div>
                        <p className="text-xs text-gray-600">{entry.detail}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AdminUnassignedVerifications = () => {
  const { token, user } = useAuth();
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cityFilter, setCityFilter] = useState('');
  const [cities, setCities] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = cityFilter ? `?city=${cityFilter}` : '';
      const [unassignedRes, partnersRes, citiesRes] = await Promise.all([
        axios.get(`${API}/admin/unassigned-verifications${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/superadmin/partners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cities`),
      ]);
      setItems(unassignedRes.data.unassigned_verifications || []);
      setPartners(partnersRes.data.partners || []);
      setCities(citiesRes.data.cities || []);
    } catch (e) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, [token, cityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const needsReassignment = items.filter(i => i.schedule?.status === 'needs_seller_reassignment');
  const waitingForPartner = items.filter(i => i.schedule?.status === 'no_partner_available');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Unassigned Verifications
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length} pending · auto-refreshes every 30s
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 h-9 text-sm"><SelectValue placeholder="All cities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" className="h-9 text-sm flex items-center gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-xl text-sm">
          <XCircle className="h-4 w-4 text-red-500" />
          <span className="font-bold text-red-700">{needsReassignment.length}</span>
          <span className="text-red-600">Need Seller Reassignment</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-xl text-sm">
          <Clock className="h-4 w-4 text-orange-500" />
          <span className="font-bold text-orange-700">{waitingForPartner.length}</span>
          <span className="text-orange-600">Retrying (2-min auto-retry)</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-sm">
          <User className="h-4 w-4 text-blue-500" />
          <span className="font-bold text-blue-700">{partners.length}</span>
          <span className="text-blue-600">Available Partners</span>
        </div>
      </div>

      {/* Needs seller reassignment (critical) */}
      {needsReassignment.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Critical — Seller Must Reassign
          </h3>
          <div className="space-y-4">
            {needsReassignment.map((item, i) => (
              <VerificationCard key={i} item={item} partners={partners}
                onManualAssign={() => {}} onRefresh={fetchData} />
            ))}
          </div>
        </div>
      )}

      {/* Waiting for partner (auto-retrying) */}
      {waitingForPartner.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-orange-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 animate-spin" /> Auto-Retrying Every 2 Minutes
          </h3>
          <div className="space-y-4">
            {waitingForPartner.map((item, i) => (
              <VerificationCard key={i} item={item} partners={partners}
                onManualAssign={() => {}} onRefresh={fetchData} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <Card><CardContent className="p-12 text-center">
          <CheckCircle className="h-14 w-14 text-green-300 mx-auto mb-4" />
          <p className="text-gray-600 font-semibold">All verifications are assigned!</p>
          <p className="text-sm text-gray-400 mt-1">No pending or unassigned verification requests</p>
        </CardContent></Card>
      )}

      {loading && items.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
};

export default AdminUnassignedVerifications;